// ── Claude Managed Agents — API Client ────────────────────────────────────────
// Base: process.env.REACT_APP_AccountAMP_Url + /api/v1/claude-managed
// Auth: Bearer token from localStorage('authToken')

import { stripAreaContext } from '../../utils/areaContext';
import { shareInFlight, forgetShared } from '../../utils/inFlight';

// The Command Center page, its sidebar and the task panel it opens each load the
// agent list within a second or two of each other — reuse one result briefly.
const REUSE_LIST_MS = 10_000;

const BASE = `${process.env.REACT_APP_MyAMP_Url ?? ''}/api/v1/claude-managed`;

function token(): string {
  return localStorage.getItem('authToken') ?? '';
}

function headers(extra: Record<string, string> = {}): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...extra };
}

// json.error is usually a plain string, but some endpoints pass through the raw
// Anthropic SDK exception instead — either as an object ({type, message, ...})
// or as that exception's stringified form, e.g.
// "Error code: 400 - {'type': 'error', 'error': {'type': 'invalid_request_error',
//  'message': 'Skill cannot reuse an existing display_title: accord form creator_51'},
//  'request_id': '...'}". Pull just the human-readable message out of either shape —
// never the type/id/request_id fields, and never "[object Object]".
function extractApiErrorMessage(rawError: unknown, status: number): string {
  if (typeof rawError === 'string') {
    const embedded = rawError.match(/'message':\s*'((?:[^'\\]|\\.)*)'/);
    if (embedded) return embedded[1].replace(/\\'/g, "'");
    return rawError;
  }
  if (rawError && typeof rawError === 'object') {
    const obj = rawError as Record<string, unknown>;
    if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;
    if (obj.error && typeof obj.error === 'object') {
      const nested = (obj.error as Record<string, unknown>).message;
      if (typeof nested === 'string' && nested.trim()) return nested;
    }
  }
  return `HTTP ${status}`;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: headers(),
    ...init,
    ...(init?.headers ? { headers: { ...headers(), ...(init.headers as Record<string, string>) } } : {}),
  });
  const json = await res.json();
  // Any write to an agent (create, update, archive, primary, tools…) must not be
  // followed by the briefly-reused agent list — drop it so the next read is fresh.
  if (init?.method && init.method !== 'GET' && path.startsWith('/agents')) forgetShared('dwAgents|');
  if (!json.success) {
    throw new Error(extractApiErrorMessage(json.error, res.status));
  }
  return json.data as T;
}

// ── Types ──────────────────────────────────────────────────────────────────────
export interface AgentStatus {
  configured: boolean;
  is_byok: boolean;
  has_vault: boolean;
  has_mcp_token: boolean;
  has_agent: boolean;
  agent_id: string | null;
  sdk_available: boolean;
}

export interface AgentSkillRecord {
  id: number;
  name: string;
  skill_type: string;
  is_active: boolean;
  created_at: string;
  skill_id?: string;
  is_persona_default?: boolean;
  config?: {
    skill_id?: string;
    version?: string;
    premade_id?: number;
    marketplace_tool_id?: number;
  };
  premade_id?: number;
  /** Set when the skill is a Marketplace copy (installed from a Marketplace listing). */
  marketplace_tool_id?: number;
}

export interface SkillLibraryItem {
  id: string;
  name: string;
  source: 'anthropic' | 'custom';
  latest_version: string;
  created_at: string;
  updated_at: string;
  custom_version?: string | null;  // v1 / v2 / … from claude_managed_custom_skills
}

export interface McpServerRecord {
  id: number;
  name: string;
  url: string;
  is_active: boolean;
  created_at: string;
}

export interface McpCredential {
  id: number;
  name: string;
  url: string;
  credential_id: string;
  is_active: boolean;
  created_at: string;
}

export interface ExternalMcpItem {
  transactionId: string;
  userId: number;
  companyId: number;
  name: string;
  description?: string;
  url: string;
  tools?: string; // JSON string of tool definitions
  needsUserCredentials?: boolean;
  isSystem?: boolean;
  // Set only when this (non-system) connection already has a My Creations
  // listing behind it (via my_tool_installations.external_mcp_transaction_id) —
  // lets the UI offer Share the same way it does for skills.
  myToolId?: string;
}

export interface AgentCustomToolRecord {
  id?: number | string;
  tool_id?: number | string;
  name?: string;
  tool_name?: string;
  title?: string;
  description?: string;
  attached?: boolean;
  is_attached?: boolean;
  isAttached?: boolean;
  source?: 'shared' | 'composio' | 'api_integration';
}

export interface AgentDetail {
  agent_id: string;
  name: string;
  model: string | { id: string; speed?: string };
  description: string;
  system: string;
  tools: unknown[];
  mcp_servers: unknown[];
  skills: unknown[];
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  harness_type?: 'Claude' | 'Hermes';
}

// ── SSE Chat event types ───────────────────────────────────────────────────────
export interface SseSessionStart        { type: 'session.start';              session_id: string; }
// Full agent response (actual API shape)
export interface SseAgentMessage        { type: 'agent.message';              session_id: string; content: Array<{ type: string; text?: string }>; }
// Agent is thinking / processing (no content)
export interface SseAgentThinking       { type: 'agent.thinking';             session_id: string; }
// Legacy / compat shapes
export interface SseAssistantMsg        { type: 'assistant.message';          session_id: string; content: Array<{ type: string; text?: string }>; }
export interface SseMessageDelta        { type: 'message.delta';              session_id: string; content: Array<{ type: string; text?: string }>; }
// Session/thread lifecycle
export interface SseStatusRunning       { type: 'session.status_running';     session_id: string; }
export interface SseStatusIdle          { type: 'session.status_idle';        session_id: string; stop_reason: { type: string } | null; }
export interface SseThreadStatusRunning { type: 'session.thread_status_running'; session_id: string; }
export interface SseThreadStatusIdle    { type: 'session.thread_status_idle'; session_id: string; stop_reason: { type: string; event_ids?: string[] } | null; }
// Agent is calling an MCP tool
export interface SseAgentMcpToolUse     { type: 'agent.mcp_tool_use';         session_id: string; id?: string; name: string; input: Record<string, unknown>; session_thread_id?: string | null; mcp_server_name?: string; }
// Agent is calling a built-in tool (bash, computer, etc.)
export interface SseAgentToolUse        { type: 'agent.tool_use';             session_id: string; id?: string; name: string; input: Record<string, unknown>; }
// Agent is calling a custom (non-MCP) tool
export interface SseAgentCustomToolUse  { type: 'agent.custom_tool_use';      session_id: string; id?: string; name: string; input: Record<string, unknown>; }
// Model processing lifecycle (use start as thinking trigger)
export interface SseSpanModelRequest    { type: 'span.model_request_start' | 'span.model_request_end'; session_id: string; id?: string; }
// Artifact persisted from an agent-generated file (upload_session_file) — the
// backend emits this alongside the tool result so the FE can render an
// attachment chip without scraping the tool-result JSON or markdown links.
// app_url is the permanent portal viewer URL; artifact_id is the artifact guid
// (usable with the /artifacts/{guid} endpoints).
// AI-112 Part 2: the same event type/shape is also emitted for Hermes-backed
// agents (_stream_hermes_chat in claudeManaged_module.py) whenever Hermes
// reports a file created during the turn — but only filename + artifact_id
// are ever sent (app_url/tool_use_id don't apply to a Hermes artifact, which
// has no Momentum-portal viewer URL; it's fetched via the dedicated
// downloadHermesArtifact proxy below instead). Every field here besides
// `type` is already optional, so the narrower Hermes shape type-checks as-is.
export interface SseArtifactCreated     { type: 'artifact.created';           session_id?: string; tool_use_id?: string; filename?: string | null; app_url?: string; artifact_id?: string | null; }
// Non-fatal per-attempt error; agent will retry automatically
export interface SseSessionError        { type: 'session.error';              session_id: string; id?: string; }
// Fatal error from our server wrapper. `error` is already user-facing text;
// `code` is the machine-readable cause (see BUDGET_REACHED_CODE and
// SESSION_TERMINATED_CODE below). `terminated` is set when the Anthropic
// session itself is gone (session.status_terminated); `detail` is the raw
// upstream reason, for logging only.
export interface SseError               { type: 'error';                      error: string; code: string; session_id?: string; detail?: string; terminated?: boolean; }

// The session hit its per-conversation spending budget (server default $8,
// CLAUDE_SESSION_BUDGET_USD) and Anthropic paused it. The backend streams this
// code both when the budget is crossed mid-turn and when a later message is
// sent to the already-paused session — every further turn on that session
// fails the same way, so the only way forward is a new conversation.
export const BUDGET_REACHED_CODE = 'BUDGET_REACHED';
// Shown only if the backend sent the code without a message (version skew).
export const BUDGET_REACHED_FALLBACK_MESSAGE =
  'This conversation has reached its default AI spending budget and has been paused. ' +
  'Please start a new conversation to continue, or contact your administrator to raise the limit.';
export const isBudgetReachedError = (evt: SseEvent): boolean =>
  (evt.type === 'error'  && evt.code === BUDGET_REACHED_CODE) ||
  (evt.type === 'answer' && evt.error_code === BUDGET_REACHED_CODE);
// The Anthropic session was terminated: an unrecoverable session error, e.g.
// the request (attached files + history) overflowed the model's context
// window. The conversation survives — the backend replays the next message in
// a fresh session — so the UI shows a "session ended" notice, not a dead end.
export const SESSION_TERMINATED_CODE = 'SESSION_TERMINATED';
export const isSessionTerminatedError = (evt: SseEvent): boolean =>
  (evt.type === 'error'  && (evt.code === SESSION_TERMINATED_CODE || !!evt.terminated)) ||
  (evt.type === 'answer' && evt.error_code === SESSION_TERMINATED_CODE);
/** User-facing text for a fatal `error` event — the backend's message when it
 * sent one, otherwise a code-specific fallback. */
export const sseErrorMessage = (evt: SseError): string => {
  if (evt.error) return evt.error;
  if (evt.code === BUDGET_REACHED_CODE) return BUDGET_REACHED_FALLBACK_MESSAGE;
  return 'Something went wrong. Please try again.';
};
// AMS-style terminal/partial answer shape — the same {response, responseStreaming,
// ai_tool, tool_history} contract chatService.ts already parses for the legacy
// chat endpoints. The multipart /claude-managed/chat contract replies in this
// shape rather than the agent.message shape above.
//
// Streaming contract: the backend opts its Managed-Agents connection into live
// answer previews, so partial answers (responseStreaming !== false) arrive as
// small text fragments as the model writes them — not one event per finished
// model message. Concatenate them in arrival order; that concatenation is the
// complete answer. The terminal event (responseStreaming === false) exists to
// carry tool_history / artifacts / chatId and its `response` is normally empty,
// so never treat it as the authoritative text when anything already streamed.
// pumpSse() below merges consecutive fragments before they reach onEvent, so a
// consumer sees a handful of batched chunks per second rather than one per token.
export interface SseAnswerToolHistoryItem { tool_name: string; tool_payload?: string; tool_response?: string; }
// Files generated this turn (upload_session_file) — carried on the terminal
// answer event (responseStreaming === false) so they can be rendered as an
// attachment list below the response. Same fields as SseArtifactCreated.
export interface SseAnswerArtifact { filename?: string | null; app_url?: string; artifact_id?: string | null; tool_use_id?: string; }
export interface SseAnswerEvent {
  type: 'answer';
  response: string;
  chatId?: string;
  conversationId?: string;
  ai_tool?: string[];
  responseStreaming?: boolean;
  error_message?: string;
  /** Machine-readable twin of error_message (e.g. BUDGET_REACHED_CODE). */
  error_code?: string;
  tool_history?: SseAnswerToolHistoryItem[];
  artifacts?: SseAnswerArtifact[];
  /** True when the finished response is worth exporting as a downloadable
   * artifact — the UI shows a "Generate artifact" button that sends
   * ARTIFACT_GENERATION_PROMPT back through streamChat. */
  can_generate_artifact?: boolean;
  /** Terminal event only: the answer closes by asking the user whether to
   * proceed (or carries a $$SHOWMODAL_…$$ marker) — the UI shows one-click
   * Yes / No replies. proceed_prompt is null when show_proceed_buttons is false. */
  show_proceed_buttons?: boolean;
  proceed_prompt?: { text: string; options: { label: string; reply: string }[] } | null;
  imageURL?: string[];
  pageURL?: string[];
}
// Human-readable running commentary emitted by the backend's support-chat SSE
// adapter (_to_support_chat_sse): the tool announcements, "Session active…",
// "Formulating response…" and raw model thinking, streamed as word-level chunks
// that the UI CONCATENATES into the ThinkingBlock body. Same contract the
// kanban stream uses, so /chat renders real thinking instead of a placeholder.
export interface SseThoughtEvent {
  type: 'thought';
  thought: string;
  chatId?: string;
  conversationId?: string;
  currentTool?: string;
  currentToolResponse?: string;
  responseStreaming?: boolean;
  estimatedSeconds?: number;
}
export type SseEvent =
  | SseSessionStart
  | SseAgentMessage
  | SseAgentThinking
  | SseThoughtEvent
  | SseAgentMcpToolUse
  | SseAgentToolUse
  | SseAgentCustomToolUse
  | SseSpanModelRequest
  | SseAssistantMsg
  | SseMessageDelta
  | SseStatusRunning
  | SseStatusIdle
  | SseThreadStatusRunning
  | SseThreadStatusIdle
  | SseArtifactCreated
  | SseSessionError
  | SseAnswerEvent
  | SseError;

// ── SSE reader ────────────────────────────────────────────────────────────────
// /chat, /chat/resume and /chat/reattach all speak the same event contract, so
// they share one reader.
//
// The backend's answer previews are best-effort: normally the text arrives as a
// steady trickle of fragments, but under load the platform sheds deltas and the
// missing tail lands as one big buffered chunk. Rendering events as they arrive
// therefore looks rough — smooth typing, a stall, then a wall of text.
//
// pumpSse smooths that out by decoupling receipt from reveal: incoming answer
// text goes into a queue, and a ticker releases it as answer chunks at a steady
// cadence. The per-tick size adapts to the backlog — a live trickle passes
// through at reading speed, while a shed-delta dump animates out over roughly a
// second instead of appearing at once. Ordering within the answer text is
// preserved exactly (only answer events carry text); the terminal answer event
// (responseStreaming === false, carrying tool_history/artifacts/chatId) is held
// until the queue drains so it still closes the turn last. All other events —
// thought, tool use, artifact chips, approval idles, errors — pass through
// immediately: they render outside the answer text, and holding them behind the
// reveal animation would delay real interactions (Approve/Deny, error display).
const ANSWER_TICK_MS    = 35;   // reveal cadence
const ANSWER_DRAIN_PART = 20;   // per tick, reveal 1/20 of the backlog…
const ANSWER_MIN_CHARS  = 4;    // …but at least this many characters

// $$CANGENERATEARTIFACTSTART$$true/false$$CANGENERATEARTIFACTEND$$ — the
// artifact-button flag the model appends to its answer. Current backends strip
// it server-side and deliver only the boolean (can_generate_artifact on the
// terminal answer event); this regex is a version-skew guard so a backend that
// still leaks the marker never gets it rendered as answer text.
const ARTIFACT_FLAG_MARKER_RE =
  /\$\$CANGENERATEARTIFACTSTART\$\$\s*(true|false)\s*\$\$CANGENERATEARTIFACTEND\$\$/gi;

async function pumpSse(res: Response, onEvent: (e: SseEvent) => void): Promise<void> {
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

  let template: SseAnswerEvent | null = null;  // latest partial's fields, reused on reveals
  let queue    = '';                           // text received but not yet revealed
  let terminal: SseAnswerEvent | null = null;  // held until the queue drains
  let markerFlag: boolean | null = null;       // flag salvaged from a leaked marker
  let timer: ReturnType<typeof setInterval> | null = null;
  let wake: (() => void) | null = null;        // resolves the end-of-stream drain wait

  const stopTimer = () => { if (timer !== null) { clearInterval(timer); timer = null; } };

  const emitTerminal = () => {
    if (!terminal) return;
    const t = terminal;
    terminal = null;
    // /chat's answer-shaped adapter folds a fatal turn error into the terminal
    // answer as error_message/error_code (e.g. BUDGET_REACHED: the session hit
    // its spending budget and was paused) and does NOT forward the underlying
    // `error` event. Re-surface it here as a `type: 'error'` event — after any
    // streamed text has been revealed, before the terminal closes the turn — so
    // every consumer keeps a single error path and a turn that produced no
    // answer text still explains itself instead of ending blank.
    if (t.error_message) {
      onEvent({ type: 'error', error: t.error_message, code: t.error_code || 'CHAT_ERROR' });
    }
    onEvent(t);
  };

  const revealTick = () => {
    if (queue && template) {
      const n = Math.max(ANSWER_MIN_CHARS, Math.ceil(queue.length / ANSWER_DRAIN_PART));
      const slice = queue.slice(0, n);
      queue = queue.slice(n);
      onEvent({ ...template, response: slice });
    }
    if (!queue) {
      stopTimer();
      emitTerminal();
      if (wake) { const w = wake; wake = null; w(); }
    }
  };
  const startTimer = () => { if (timer === null) timer = setInterval(revealTick, ANSWER_TICK_MS); };

  // Correctness path — abort or a thrown error mustn't swallow received text,
  // so the animation is skipped and everything left is delivered at once.
  const flushAllNow = () => {
    stopTimer();
    if (queue && template) { onEvent({ ...template, response: queue }); queue = ''; }
    emitTerminal();
  };

  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let   buffer  = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';          // keep incomplete last line

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const raw = trimmed.slice(5).trim();
        if (!raw || raw === '[DONE]') continue;
        let evt: SseEvent;
        try { evt = JSON.parse(raw) as SseEvent; } catch { continue; /* skip malformed */ }
        if (evt.type === 'answer' && evt.responseStreaming !== false && evt.response) {
          // Marker never spans events (the backend delivers it inside one
          // buffered chunk), so per-event stripping is safe.
          const text = evt.response.replace(ARTIFACT_FLAG_MARKER_RE, (_all, flag: string) => {
            markerFlag = flag.toLowerCase() === 'true';
            return '';
          });
          if (text) {
            template = evt;
            queue += text;
            startTimer();
          }
        } else if (evt.type === 'answer' && evt.responseStreaming === false) {
          terminal = { ...evt, response: (evt.response ?? '').replace(ARTIFACT_FLAG_MARKER_RE, '') };
          if (terminal.can_generate_artifact === undefined && markerFlag !== null) {
            // Backend didn't send the boolean — fall back to the salvaged flag.
            terminal.can_generate_artifact = markerFlag;
          }
          if (!queue) { stopTimer(); emitTerminal(); }
          // else: revealTick emits it once the queue drains
        } else {
          onEvent(evt);
        }
      }
    }
    // Stream closed with text still animating — let the reveal finish before
    // resolving, so the caller's "stream ended" logic doesn't cut it off. The
    // adaptive tick bounds this wait to roughly a second regardless of size.
    if (queue || terminal) {
      startTimer();
      await new Promise<void>((resolve) => { wake = resolve; });
    }
  } finally {
    flushAllNow();
  }
}

export interface EnvironmentRecord {
  environment_id: string;
  name: string;
  description: string;
}

// ── Status ─────────────────────────────────────────────────────────────────────
export const getStatus       = () => api<AgentStatus>('/status');
export const getEnvironments = () => api<EnvironmentRecord[]>('/environments');

// ── Configure (admin activate) ─────────────────────────────────────────────────
export interface ConfigureResult {
  message:        string;
  is_byok:        boolean;
  vault_id:       string;
  environment_id: string | null;
  agent_id:       string | null;
  clone_template: boolean;
  mcp_token:      string;
}
export const configureUser = (
  user_id: string,
  api_key: string | null,
  clone_template = true,
  mcp_token?: string,
  environment_id?: string,
) =>
  api<ConfigureResult>('/configure', {
    method: 'POST',
    body: JSON.stringify({
      user_id,
      api_key:        api_key ?? null,
      clone_template,
      ...(mcp_token      ? { mcp_token }      : {}),
      ...(environment_id ? { environment_id } : {}),
    }),
  });

export const configureDwSetup = (nc_token: string, clone_template = true) =>
  api<{ message?: string; configured?: boolean; agent_id?: string | null }>('/configure', {
    method: 'POST',
    body: JSON.stringify({ nc_token, clone_template }),
  });

// ── Agent ──────────────────────────────────────────────────────────────────────
export const getAgent       = (agentId: string) => api<AgentDetail>(`/agents/${agentId}`);
export const updateAgent    = (agentId: string, patch: { name?: string; system?: string; description?: string; model?: string }) =>
  api<AgentDetail>(`/agents/${agentId}`, { method: 'PUT', body: JSON.stringify(patch) });
export const enableAgent    = (name?: string)   =>
  api<{ agent_id: string; name: string; version: string | null }>('/agents/enable', {
    method: 'POST', body: JSON.stringify({ name: name ?? null }),
  });

// ── DW Agent CRUD ──────────────────────────────────────────────────────────────
export interface DwAgentModelObject {
  id: string;
  speed?: string | null;
  effort?: { type?: string | null } | string | null;
  inference_geo?: string | null;
}

export interface DwAgentRecord {
  agent_id: string;
  name: string;
  description?: string;
  system?: string;
  persona_key?: string | null;
  // GET returns the object form: { id, effort: { type: 'xhigh' }, speed: 'standard',
  // inference_geo }. Create/update take the id as a plain string plus top-level
  // effort / speed (see createDwAgent).
  model?: string | DwAgentModelObject;
  // Thinking effort (low | medium | high | xhigh | max), or the string "default"
  // when the agent has no override — GET always sends one (backend _agent_to_dict).
  effort?: string | null;
  // 'standard' | 'fast' — fast mode (Opus 4.8 only).
  speed?: string | null;
  soul?: string;       // input-only — not returned by GET
  heartbeat?: string;  // input-only — not returned by GET
  is_system?: boolean;
  is_premade?: boolean;
  is_archived?: boolean | null;
  is_primary_agent?: boolean;
  is_voice_agent?: boolean;
  created_at?: string;
  updated_at?: string;
  // 'Claude' (default, Anthropic-managed agent) or 'Hermes' (a
  // claw.insuranceagency.ai computer-use agent) — every user gets one Hermes
  // agent alongside their Claude agents, additive, not a separate mode.
  harness_type?: 'Claude' | 'Hermes';
}

export const listDwAgents = (includeArchived = false, compact = false, slim = false) => {
  const params = new URLSearchParams();
  if (includeArchived) params.set('include_archived', 'true');
  if (compact) params.set('compact', 'true');
  if (slim) params.set('slim', 'true');
  const qs = params.toString();
  const path = `/agents${qs ? `?${qs}` : ''}`;
  const token = localStorage.getItem('authToken') || '';
  const cacheKey = `dwAgents|${path}|${token}`;

  // Shared across concurrent callers via shareInFlight.
  return shareInFlight(cacheKey, () => api<DwAgentRecord[]>(path), REUSE_LIST_MS)
    .then((records) => (Array.isArray(records) ? [...records] : records));
};

// The Kanban task feed lives on a different backend/base than this file's
// `api()` helper (REACT_APP_Auth_Url vs REACT_APP_MyAMP_Url + claude-managed),
// so this reads it directly rather than forcing it through `api()`.
const KANBAN_DONE_STATUS_ID = '44444444-4444-4444-4444-444444444444';
// Same 4 Kanban status GUIDs duplicated in MomentumCommandPage.tsx,
// CommandIssuePanel.tsx, and communicationService.ts — not re-exported from
// any of those today, so this follows the same established (if repetitive)
// convention rather than reaching into a page component for a constant.
const KANBAN_ALL_STATUS_IDS = [
  '11111111-1111-1111-1111-111111111111',
  '22222222-2222-2222-2222-222222222222',
  '33333333-3333-3333-3333-333333333333',
  KANBAN_DONE_STATUS_ID,
];
// The confirmed Digital-Workforce assignee-type codes (isDwAssigneeType in
// MomentumCommandPage.tsx) — assigneeType 1 is a human/other assignee.
const DW_ASSIGNEE_TYPES = ['2', '3'];
const KANBAN_IN_PROGRESS_STATUS_ID = '22222222-2222-2222-2222-222222222222';

export interface DwAgentActivity {
  /** Open (not-done) task count — todo, in progress, or in review. */
  openCount: number;
  /** Title of a real in-progress task, if this agent has one right now. */
  currentTask: string | null;
}

/**
 * Real per-agent activity from the same Kanban feed Command Center and
 * Communication Center use — the honest substitute for a live "is this
 * agent active right now" status, which doesn't exist anywhere in this
 * backend (see SidebarDigitalTeam.tsx for why). `currentTask` is only ever
 * set from a task actually in the "in progress" status — never inferred or
 * guessed — so the sidebar can say what an agent is doing instead of just
 * how many things are queued.
 */
export function listDwAgentActivity(): Promise<Record<string, DwAgentActivity>> {
  const authToken = localStorage.getItem('authToken') || '';
  return shareInFlight(`dwAgentActivity|${authToken}`, async () => {
    const params = new URLSearchParams({ page: '1', pageSize: '200' });
    KANBAN_ALL_STATUS_IDS.forEach(id => params.append('statusIds', id));
    DW_ASSIGNEE_TYPES.forEach(t => params.append('assigneeTypes', t));

    const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/momentumkanban/tasks?${params}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) throw new Error(`fetch agent workload failed: ${res.status}`);
    const json = await res.json();
    const rows: any[] = Array.isArray(json) ? json : (json?.data ?? []);

    const activity: Record<string, DwAgentActivity> = {};
    for (const row of rows) {
      if (row.statusId === KANBAN_DONE_STATUS_ID) continue;
      const agentId = row.agentId ?? row.agent_id ?? row.AgentId ?? row.agent?.id;
      if (!agentId) continue;
      const entry = activity[agentId] ?? { openCount: 0, currentTask: null };
      entry.openCount += 1;
      if (!entry.currentTask && row.statusId === KANBAN_IN_PROGRESS_STATUS_ID && row.title) {
        entry.currentTask = row.title;
      }
      activity[agentId] = entry;
    }
    return activity;
  }, REUSE_LIST_MS);
}

// Runtime settings sent on create/update alongside the model id (a plain string —
// the API rejects an object for `model`). `effort` is null to clear it on a model
// without effort support; `speed` is 'standard' | 'fast'.
export type DwAgentSpeed = 'standard' | 'fast';

export const createDwAgent = (params: {
  name: string;
  model?: string;
  effort?: string | null;
  speed?: DwAgentSpeed;
  soul?: string;
  heartbeat?: string;
  system?: string;
  description?: string;
}) => api<DwAgentRecord>('/agents', { method: 'POST', body: JSON.stringify(params) });

export const updateDwAgent = (agentId: string, patch: {
  name?: string;
  model?: string;
  effort?: string | null;
  speed?: DwAgentSpeed;
  soul?: string;
  heartbeat?: string;
  system?: string;
  description?: string;
}) => api<DwAgentRecord>(`/agents/${agentId}`, { method: 'PUT', body: JSON.stringify(patch) });

// Single agent, live from Anthropic — unlike the list (DB-backed, model as a bare
// id string) this returns the full model object incl. effort / speed.
export const getDwAgent = (agentId: string) => api<DwAgentRecord>(`/agents/${agentId}`);

export const archiveDwAgent = (agentId: string) =>
  api<{ message?: string; agent_id?: string }>(`/agents/${agentId}/archive`, { method: 'POST' });

export const setPrimaryDwAgent = (agentId: string) =>
  api<{ message?: string; agent_id?: string }>(`/agents/${agentId}/primary`, { method: 'PUT' });

// ── Skills ─────────────────────────────────────────────────────────────────────
export const getAgentSkills   = (agentId: string) => api<AgentSkillRecord[]>(`/agents/${agentId}/skills`);
/** Premade entry as returned inside GET /skills (name only — no description/content). */
export interface PremadeLibraryItem {
  id: number;
  name: string;
  skill_id: string | null;
  source: 'premade';
  version: string | null;
}
/**
 * A Marketplace copy in GET /skills: a skill installed from a Marketplace listing.
 * Assign-only, like a premade - its content stays private to the publisher.
 */
export interface MarketplaceLibraryItem {
  id: string;           // skill_id of the Marketplace copy
  name: string;
  source: 'marketplace';
  version: string | null;
  tool_id: number;      // the listing (my_tools.id) it was installed from
}
/**
 * Skill library. Pass agentId to scope `anthropic` + `custom` to the skills
 * assigned to that agent (GET /skills?agent_id=...); `premade` is always the
 * full assign-only catalog, and `marketplace` every Marketplace skill the user has
 * installed. Omit agentId for the library-wide view.
 */
export const getSkillLibrary  = (agentId?: string) =>
  api<{ anthropic: SkillLibraryItem[]; custom: SkillLibraryItem[]; premade?: PremadeLibraryItem[]; marketplace?: MarketplaceLibraryItem[] }>(
    agentId ? `/skills?agent_id=${encodeURIComponent(agentId)}` : '/skills',
  );
export const assignSkill = (agentId: string, item: Pick<SkillLibraryItem, 'id' | 'name'> & { source: SkillLibraryItem['source'] | 'marketplace' }) => {
  const body = {
    name: item.name,
    skill_id: item.id,
    // Only an Anthropic-authored skill is type "anthropic". Every other library source
    // (custom, custom_fork, premade, marketplace) is a skill uploaded to the account, i.e. "custom":
    // a custom skill posted as "anthropic" made every chat session for the agent fail
    // with NOT_FOUND. The backend re-derives the type from the skill itself as well.
    type: item.source === 'anthropic' ? 'anthropic' : 'custom',
  };
  return api<{ message: string; agent_id: string }>(`/agents/${agentId}/skills`, {
    method: 'POST', body: JSON.stringify(body),
  });
};
export const removeSkill      = (agentId: string, skillId: number) =>
  api<{ message: string }>(`/agents/${agentId}/skills/${skillId}`, { method: 'DELETE' });

/** Assign a premade skill directly (no library copy). Creates it in Anthropic and tracks premade_id. */
export const assignPremadeToAgent = (agentId: string, premadeId: number) =>
  api<{ message: string; agent_id: string }>(`/agents/${agentId}/skills/from-premade`, {
    method: 'POST', body: JSON.stringify({ premade_id: premadeId }),
  });

/** Permanently delete a custom skill from Anthropic and all agents. */
export const deleteSkillFromLibrary = (skillId: string) =>
  api<{ id: string; deleted: boolean; agents_updated: number }>(`/skills/library/${skillId}`, { method: 'DELETE' });

// ── MCP servers ────────────────────────────────────────────────────────────────
export const getAgentMcpServers = (agentId: string) => api<McpServerRecord[]>(`/agents/${agentId}/mcp-servers`);
export const getMcpCredentials  = () => api<McpCredential[]>('/mcp-credentials');
export const assignMcpServer    = (agentId: string, name: string, url: string, transactionId?: string) =>
  api<{ message: string; agent_id: string }>(`/agents/${agentId}/mcp-servers`, {
    method: 'POST', body: JSON.stringify({ name, url, ...(transactionId ? { transaction_id: transactionId } : {}) }),
  });
export const removeMcpServer    = (agentId: string, serverId: number) =>
  api<{ message: string }>(`/agents/${agentId}/mcp-servers/${serverId}`, { method: 'DELETE' });
export const getAgentCustomTools = (agentId: string, attachedOnly = false) =>
  api<AgentCustomToolRecord[]>(`/agents/${agentId}/custom-tools${attachedOnly ? '?attached_only=true' : ''}`);
export const setAgentCustomTools = (agentId: string, tools: string[]) =>
  api<{ message: string; agent_id: string; tools: string[] }>(`/agents/${agentId}/custom-tools`, {
    method: 'POST', body: JSON.stringify({ tools }),
  });

// ── API Integrations ────────────────────────────────────────────────────────────
// Separate from setAgentCustomTools above — that endpoint refuses to touch
// system/persona Digital Workforce agents, and all 5 real agents are system
// agents. These two are scoped narrowly to integration tool names and work on
// any agent, same as Skills already do.
export const enableIntegrationForAgent = (agentId: string, integrationId: number) =>
  api<{ message: string; agent_id: string }>(`/agents/${agentId}/integrations/${integrationId}/enable`, { method: 'POST' });
export const disableIntegrationForAgent = (agentId: string, integrationId: number) =>
  api<{ message: string; agent_id: string }>(`/agents/${agentId}/integrations/${integrationId}`, { method: 'DELETE' });
export const addMcpCredential   = (name: string, url: string, token?: string) =>
  api<{ message: string; url: string }>('/mcp-credentials', {
    method: 'POST', body: JSON.stringify({ name, url, ...(token ? { token } : {}) }),
  });
/** Attach an MCP already covered by a stored credential (added via addMcpCredential) — no
 * transaction_id/registry lookup, unlike assignMcpServer. Used by the Share Agent import
 * flow's credentials modal, where the user typed the credential in directly. */
export const attachExistingMcpServer = (agentId: string, name: string, url: string) =>
  api<{ message: string; agent_id: string }>(`/agents/${agentId}/mcp-servers/attach-existing`, {
    method: 'POST', body: JSON.stringify({ name, url }),
  });

// ── Agent sharing (AI-93 item 5) ────────────────────────────────────────────────
export interface AgentShareSnapshot {
  name: string;
  system: string | null;
  soul: string | null;
  heartbeat: string | null;
  description: string | null;
  model: string | null;
  skills: { name: string; content: string }[];
  // platform_provided: one of the MCP servers every agent gets automatically (MyAMP marks
  // these on read). needs_auth is true for them too, but the installer never adds a key.
  mcp_servers: { name: string; url: string; needs_auth: boolean; platform_provided?: boolean }[];
  // API integrations turned on for the agent, definitions frozen like skill content.
  // Missing on snapshots made before integrations travelled with agents.
  integrations?: BundledIntegration[];
  // "Your Rules" from the agent's Memory tab — user-authored config, same category
  // as soul/heartbeat. Deliberately excludes "Learned Rules" (auto-summarized from
  // the sharer's own chat history — wouldn't apply to a fresh recipient agent).
  user_rules: string | null;
}
export interface BundledIntegration {
  source_integration_id: number;
  name: string;
  slug: string;
  icon?: string | null;
  // Whether the API takes a key at all - whoever installs the agent adds their own.
  needs_auth: boolean;
  installer_key_scope?: string;
  definition?: {
    description?: string | null;
    base_url?: string | null;
    auth_type: string;
    auth_location?: string | null;
    auth_param_name?: string | null;
    endpoints: { name: string; summary?: string | null; description?: string | null; http_method: string; path_template: string; body_format?: string }[];
  };
}
/** What happened to one bundled integration on import. */
export interface ImportedIntegration {
  integration_id?: number;
  name: string;
  // A key is needed and this person's calls don't have one yet.
  needs_key?: boolean;
  // true: they got their own copy; false: the agent uses the one they already had.
  copied?: boolean;
  error?: string;
}
export interface AgentShareRecord {
  id: number;
  token: string;
  scope: 'agency' | 'anyone';
  is_active?: boolean;
  created_at?: string;
}
export interface AgentImportResult {
  agent_id: string;
  name: string;
  attached_mcps: { name: string; url: string }[];
  needs_setup_mcps: { name: string; url: string }[];
  integrations?: ImportedIntegration[];
}

export const shareAgent = (agentId: string, scope: 'agency' | 'anyone') =>
  api<AgentShareRecord>(`/agents/${agentId}/share`, { method: 'POST', body: JSON.stringify({ scope }) });

/** Read-only: what a fresh shareAgent() call would produce right now, without
 * minting anything. Used by the marketplace wizard's "Update" button to detect
 * whether the live agent has drifted since the listing's snapshot was last saved. */
export const previewAgentShare = (agentId: string) =>
  api<{ snapshot: AgentShareSnapshot }>(`/agents/${agentId}/share-preview`);

export const listAgentShares = (agentId: string) => api<AgentShareRecord[]>(`/agents/${agentId}/shares`);

export const revokeAgentShare = (shareId: number) =>
  api<{ message: string }>(`/agent-shares/${shareId}`, { method: 'DELETE' });

export const regenerateAgentShare = (shareId: number) =>
  api<AgentShareRecord>(`/agent-shares/${shareId}/regenerate`, { method: 'POST' });

/** Re-freezes a share's snapshot from the agent's current live state — same
 * token, same URL, content updated in place. Unlike regenerate (new token,
 * breaks any link already sent out), this is for "the agent changed since I
 * shared it" without invalidating what's already been distributed. */
export const refreshAgentShare = (shareId: number) =>
  api<{ id: number; message: string }>(`/agent-shares/${shareId}/refresh`, { method: 'POST' });

export const resolveAgentShare = (token: string) =>
  api<{ share_id: number; source_agent_id: string | null; snapshot: AgentShareSnapshot }>(`/agent-shares/resolve/${token}`);

export const importAgentShare = (token: string, skills: { name: string; skill_id: string }[], name?: string) =>
  api<AgentImportResult>(`/agent-shares/${token}/import`, {
    method: 'POST', body: JSON.stringify({ skills, name }),
  });

// ── External MCP servers (platform catalog) ────────────────────────────────────
export const getExternalMcpList = async (): Promise<ExternalMcpItem[]> => {
  const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/externalmcp/list`, {
    headers: { Authorization: `Bearer ${token()}` },
  });
  const json = await res.json();
  // Handle both { data: [...] } and direct array responses
  const list = Array.isArray(json) ? json : (json.data ?? json.result ?? []);
  return list as ExternalMcpItem[];
};

// ── Skill library ───────────────────────────────────────────────────────────────

export const getSkillContent = (skillId: string) =>
  api<{ content: string | null; display_title: string | null }>(`/skills/library/${skillId}/content`);

/** Update an existing custom skill — backend deletes old + recreates with new content. Returns new SkillLibraryItem (new ID). */
export const updateSkillFromText = (
  skillId: string,
  displayTitle: string,
  text: string,
  companyWide = false,
): Promise<SkillLibraryItem> =>
  api<SkillLibraryItem>(`/skills/library/${skillId}`, {
    method: 'PUT',
    body: JSON.stringify({ display_title: displayTitle, text, company_wide: companyWide }),
  });

/** Create a skill from Markdown text — backend packages it as skill.md in a zip. */
export const createSkillFromText = (
  displayTitle: string,
  text: string,
  companyWide = false,
  premadeId?: number,
): Promise<SkillLibraryItem> =>
  api<SkillLibraryItem>('/skills/library/from-text', {
    method: 'POST',
    body: JSON.stringify({
      display_title: displayTitle,
      text,
      company_wide: companyWide,
      ...(premadeId != null ? { premade_id: premadeId } : {}),
    }),
  });

export const createSkillInLibrary = async (displayTitle: string, file: File, companyWide = false): Promise<SkillLibraryItem> => {
  const form = new FormData();
  form.append('display_title', displayTitle);
  form.append('file', file);
  form.append('company_wide', String(companyWide));
  const res = await fetch(`${BASE}/skills/library`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token()}` }, // NO Content-Type — browser sets multipart boundary
    body: form,
  });
  const json = await res.json();
  if (!json.success) throw new Error(extractApiErrorMessage(json.error, res.status));
  return json.data as SkillLibraryItem;
};

/** Result of POST /skills/library/convert-document: a document (PDF, Word, PowerPoint, Excel,
 * CSV, HTML, TXT, ...) converted to Markdown so its contents can be dropped into a skill prompt.
 * Conversion only - nothing is stored until the skill itself is saved. */
export interface ConvertedSkillDocument {
  filename: string;
  markdown: string;
  chars: number;
  /** true when the text was cut at max_chars - the rest belongs in a Knowledge Base file. */
  truncated: boolean;
  max_chars: number;
}
/** File types the convert-document endpoint accepts (MarkItDown converters). */
export const SKILL_DOCUMENT_ACCEPT = '.pdf,.docx,.pptx,.xlsx,.xls,.csv,.html,.htm,.txt,.md,.json,.xml,.epub,.msg';
export const convertDocumentToMarkdown = async (file: File): Promise<ConvertedSkillDocument> => {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}/skills/library/convert-document`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token()}` }, // NO Content-Type — browser sets multipart boundary
    body: form,
  });
  const json = await res.json();
  if (!json.success) throw new Error(extractApiErrorMessage(json.error, res.status));
  return json.data as ConvertedSkillDocument;
};

// ── Agency Memory Documents ────────────────────────────────────────────────────
// Persistent knowledge base files Claude-managed agents draw on as agency-wide
// context. `path` is the caller-assigned unique key used to update/delete a doc.
export interface AgencyMemoryDocument {
  path: string;
  title?: string;
  description?: string;
  file_url?: string;
  filename?: string;
  size?: number;
  created_at?: string;
  updated_at?: string;
}

// Deliberately bypasses the api<T>() helper — that helper unwraps {success,
// data} assuming `data` IS the array, but this endpoint nests it one level
// deeper as {success, data: {documents: [...]}}. Accept a few shapes so
// whichever nesting the backend actually uses doesn't get silently swallowed.
export async function listAgencyMemoryDocuments(): Promise<AgencyMemoryDocument[]> {
  const res = await fetch(`${BASE}/agency/memory/documents`, { headers: headers() });
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(extractApiErrorMessage(json?.error, res.status));
  }
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.data?.documents)) return json.data.documents;
  if (Array.isArray(json?.documents)) return json.documents;
  return [];
}

export async function uploadAgencyMemoryDocument(opts: {
  file?: File;
  fileUrl?: string;
  title: string;
  description?: string;
  path: string;
  // Agency Document (Profile > Agency Documents) the file comes from. The
  // backend only lets that document's uploader send it to memory.
  transactionId?: string;
}): Promise<AgencyMemoryDocument> {
  const form = new FormData();
  if (opts.file) form.append('file', opts.file);
  if (opts.fileUrl) form.append('file_url', opts.fileUrl);
  form.append('title', opts.title);
  if (opts.description) form.append('description', opts.description);
  form.append('path', opts.path);
  if (opts.transactionId) form.append('transaction_id', opts.transactionId);
  const res = await fetch(`${BASE}/agency/memory/documents`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token()}` }, // NO Content-Type — browser sets multipart boundary
    body: form,
  });
  const json = await res.json();
  if (!json.success) throw new Error(extractApiErrorMessage(json.error, res.status));
  return json.data as AgencyMemoryDocument;
}

export const updateAgencyMemoryDocument = (payload: {
  path: string;
  file_url?: string;
  title?: string;
  description?: string;
  // Agency Document the memory copy came from - uploader-only on the backend.
  transaction_id?: string;
}) =>
  api<AgencyMemoryDocument>('/agency/memory/documents', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const deleteAgencyMemoryDocument = (path: string, transactionId?: string) =>
  api<{ deleted?: boolean }>('/agency/memory/documents', {
    method: 'DELETE',
    // transaction_id: the Agency Document the memory copy came from - the
    // backend only lets its uploader remove it from memory.
    body: JSON.stringify({ path, transaction_id: transactionId }),
  });

// ── Tool action approval ──────────────────────────────────────────────────────
export async function resumeWithAction(
  sessionId: string,
  eventIds: string[],
  approve: boolean,
  onEvent: (e: SseEvent) => void,
  signal?: AbortSignal,
  sessionThreadIds?: (string | null)[],
): Promise<void> {
  const res = await fetch(`${BASE}/chat/resume`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({
      session_id: sessionId,
      event_ids: eventIds,
      approve,
      ...(sessionThreadIds ? { session_thread_ids: sessionThreadIds } : {}),
    }),
    signal,
  });
  await pumpSse(res, onEvent);
}

// ── Live turn re-attach (page refresh mid-generation) ─────────────────────────
// A /chat turn keeps generating server-side even if the page is refreshed (the
// backend decouples generation from the SSE connection). getActiveTurn asks
// whether a turn is still in flight for a conversation; reattachChat re-opens
// a live SSE stream of that turn using the same event contract as streamChat.
export interface ActiveTurnInfo {
  active: boolean;
  /** The in-flight turn's user prompt — render it as the pending user bubble. */
  question?: string;
  /** chatId the backend will persist the turn under. */
  chat_id?: string;
  started_at?: string | null;
}

export const getActiveTurn = (conversationId: string) =>
  api<ActiveTurnInfo>(`/chat/active/${conversationId}`);

/** Stop the agent's in-flight turn (Managed Agents user.interrupt). The
 * session runs to a safe boundary and goes idle, the open /chat stream then
 * closes normally with whatever text was produced, and the conversation stays
 * usable. Pass the conversationId sent on /chat (a raw Anthropic session id
 * also works). Rejects with 409 when the turn already finished. */
export const interruptChat = (conversationId: string) =>
  api<{ interrupted: boolean; session_id: string }>('/chat/interrupt', {
    method: 'POST',
    body: JSON.stringify({ conversationId }),
  });

export async function reattachChat(
  conversationId: string,
  onEvent: (e: SseEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${BASE}/chat/reattach/${conversationId}`, {
    headers: headers(),
    signal,
  });
  await pumpSse(res, onEvent);
}

// ── Hermes artifact download (AI-112 Part 2) ───────────────────────────────────
// MyAMP proxies this — it never talks to MomentumAMPApi (which holds the
// Hermes key) directly from the browser. See api_hermes_artifact_download in
// claudeManaged_module.py.
/** Downloads one Hermes-generated file for the click-to-download artifact chip.
 * Not a critical path — returns null on any non-OK/failed response instead of
 * throwing, so a bad download degrades quietly rather than breaking the chat. */
export async function downloadHermesArtifact(
  agentId: string,
  artifactId: string,
): Promise<{ blob: Blob; filename?: string } | null> {
  try {
    const res = await fetch(`${BASE}/hermes/${agentId}/artifacts/${artifactId}/download`, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const filename = match ? decodeURIComponent(match[1]) : undefined;
    return { blob, filename };
  } catch {
    return null;
  }
}

// ── Premade (org) skills ───────────────────────────────────────────────────────
export interface PremadeSkillRecord {
  id: number;
  title: string;
  description: string;
  content: string;
}
export const getPremadeSkills = () => api<PremadeSkillRecord[]>('/skills/premade');

// ── Tool approval modes ────────────────────────────────────────────────────────
export type ToolApprovalMode = 'ask' | 'approved' | 'restrict';
export const getToolApprovals  = (agentId: string) =>
  api<Record<string, ToolApprovalMode>>(`/agents/${agentId}/tool-approvals`);
export const saveToolApprovals = (agentId: string, approvals: Record<string, ToolApprovalMode>) =>
  api<{ message: string; count: number }>(`/agents/${agentId}/tool-approvals`, {
    method: 'PUT', body: JSON.stringify({ approvals }),
  });

// ── Memory (one store per agent — memstore -- <ABBR> -- <user_id>) ────────────
export interface AgentMemory {
  user_id?: string;
  agent_id?: string;
  agent_name?: string;
  persona_key?: string | null;
  name?: string;
  memory_store_id?: string | null;
  user_added_rules?: string;
  /** Read-only — learned from the agent's past sessions by run-summarization, not user-editable. */
  chat_summary_rules?: string;
  last_summarized_at?: string;
  chat_created_watermark?: string;
  kanban_updated_watermark?: string;
}
/** Memory views of every agent belonging to the caller. */
export const getAllAgentMemories = () =>
  api<{ user_id: string; memories: AgentMemory[] }>('/memory');
/** Memory view of one agent. */
export const getAgentMemory = (agentId: string) =>
  api<AgentMemory>(`/memory/${agentId}`);
export const updateMemoryUserRules = (agentId: string, rules: string) =>
  api<{ agent_id: string; user_added_rules: string }>('/memory/user-rules', {
    method: 'PUT',
    body: JSON.stringify({ agent_id: agentId, user_added_rules: rules }),
  });
export const runMemorySummarization = (agentId: string) =>
  api<{ user_id?: string; agent_id?: string; status?: string; message?: string }>(
    '/memory/run-summarization',
    { method: 'POST', body: JSON.stringify({ agent_id: agentId }) },
  );

// ── Allowed domains (web_fetch allow-list, per agent) ─────────────────────────
// A web_fetch call passes two gates and BOTH must allow the host: the
// environment's networking.allowed_hosts (shared by every agency on the
// company environment, 16 max) and the agent's own web_fetch allowed_domains
// (64 max, covers subdomains). POST writes THIS agent's list only - the
// environment is read for the environment_blocked report, never written, so
// no other agent gains reach; a domain the environment does not cover stays
// blocked until an admin adds it there. GET shows both lists. Hostnames only -
// the backend rejects URLs with ports, paths, wildcards and IPs (a pasted
// https:// prefix is tolerated).
export interface AllowedDomainsEnvironment {
  environment_id: string | null;
  shared_company_environment: boolean;
  allowed_hosts_max: number;
  name?: string;
  networking?: string;          // 'limited' | 'unrestricted'
  allowed_hosts?: string[];
}
export interface AgentAllowedDomains {
  agent_id: string;
  name: string;
  is_system: boolean;
  is_voice_agent: boolean;
  has_toolset: boolean;
  web_fetch_enabled: boolean;
  allowed_domains: string[];
  blocked_domains: string[];
  /** allowed_domains entries the environment's host list does not cover (limited networking only). */
  environment_blocked: string[];
  allowed_domains_max: number;
  environment: AllowedDomainsEnvironment;
}
export interface AddAgentAllowedDomainsResult {
  dry_run: boolean;
  domains: string[];
  include_subdomains: boolean;
  environment: AllowedDomainsEnvironment & {
    added?: string[];
    already_present?: string[];
    skipped?: string;
    updated?: boolean;
  };
  agent: {
    agent_id: string;
    name: string;
    status: 'updated' | 'skipped' | 'failed';
    web_fetch_enabled?: boolean;
    allowed_domains?: string[];
    added?: string[];
    already_present?: string[];
    seeded_from_environment?: string[];
    was_unrestricted?: boolean;
    environment_blocked?: string[];
    skip_reason?: string;
    error?: string;
  };
}
export const getAgentAllowedDomains = (agentId: string) =>
  api<AgentAllowedDomains>(`/agents/${agentId}/allowed-domains`);
export const addAgentAllowedDomains = (agentId: string, domains: string[]) =>
  api<AddAgentAllowedDomainsResult>(`/agents/${agentId}/allowed-domains`, {
    method: 'POST', body: JSON.stringify({ domains }),
  });

// Workforce-wide allow-list (Profile > Allowed Domains): adding a domain writes
// it to the environment's allowed_hosts AND to every Claude agent the user owns
// (Hermes agents are skipped). GET reports the environment plus each agent.
export interface WorkforceAgentDomains {
  agent_id: string;
  name: string;
  is_system: boolean;
  is_voice_agent: boolean;
  has_toolset?: boolean;
  web_fetch_enabled?: boolean;
  allowed_domains?: string[];
  blocked_domains?: string[];
  /** Set when the agent could not be read. */
  error?: string;
}
export interface WorkforceAllowedDomains {
  environment: AllowedDomainsEnvironment;
  agents: WorkforceAgentDomains[];
  allowed_domains_max: number;
}
export interface AddWorkforceAgentResult extends Partial<WorkforceAgentDomains> {
  agent_id: string;
  name: string;
  added?: string[];
  already_present?: string[];
  seeded_from_environment?: string[];
  environment_blocked?: string[];
  skip_reason?: string;
}
export interface AddAllowedDomainsResult {
  dry_run: boolean;
  domains: string[];
  include_subdomains: boolean;
  environment: AllowedDomainsEnvironment & {
    added?: string[];
    already_present?: string[];
    skipped?: string;
    updated?: boolean;
  };
  agents: {
    total: number;
    updated: number;
    skipped: number;
    failed: number;
    details: { updated: AddWorkforceAgentResult[]; skipped: AddWorkforceAgentResult[]; failed: AddWorkforceAgentResult[] };
  };
}
export const getAllowedDomains = () =>
  api<WorkforceAllowedDomains>('/allowed-domains');
export const addAllowedDomains = (domains: string[], includeSubdomains = false) =>
  api<AddAllowedDomainsResult>('/allowed-domains', {
    method: 'POST', body: JSON.stringify({ domains, include_subdomains: includeSubdomains, dry_run: false }),
  });

// ── Email signature (per user; the server appends it to every email any agent sends) ──
export interface EmailSignatureRecord {
  user_id?: string;
  /** Inline-styled HTML; '' when none is saved. */
  signature_html: string;
  max_chars?: number;
}
export const getEmailSignature = () =>
  api<EmailSignatureRecord>('/email-signature');
export const updateEmailSignature = (signatureHtml: string) =>
  api<EmailSignatureRecord>('/email-signature', {
    method: 'PUT',
    body: JSON.stringify({ signature_html: signatureHtml }),
  });

// ── Session History ────────────────────────────────────────────────────────────
export interface ChatSessionRecord {
  session_id:     string;
  agent_id:       string | null;
  title:          string;
  message_count:  number;
  created_at:     string;
  last_active_at: string;
}
export const getSessions    = (limit = 50, offset = 0) =>
  api<ChatSessionRecord[]>(`/sessions?limit=${limit}&offset=${offset}`);
export const deleteSession  = (sessionId: string) =>
  api<{ deleted: string }>(`/sessions/${sessionId}`, { method: 'DELETE' });
export const renameSession  = (sessionId: string, title: string) =>
  api<{ updated: string }>(`/sessions/${sessionId}/title`, { method: 'PATCH', body: JSON.stringify({ title }) });

// ── Agent Activity (Kanban tasks / Routine runs / Chats / Summary) ─────────────
// Confirmed live shape: status is status_id (reuses the MomentumKanban board's
// GUIDs — 11111111.../22222222.../33333333.../44444444... — not a word status).
export interface AgentKanbanTaskRecord {
  id: string | number;
  task_number?: string;
  title: string;
  description?: string;
  status_id?: string;
  agent_name?: string;
  company_id?: string;
  created_at: string;
  updated_at?: string;
}

export interface AgentRoutineRunRecord {
  id: string | number;
  name?: string;
  routine_id?: string;
  status?: string;
  agent_name?: string;
  started_at?: string;
  finished_at?: string;
  duration_sec?: number;
  notes?: string;
}

/** The list endpoints may come back as a bare array or wrapped in an object
 * (e.g. { tasks: [...] } / { items: [...] }) — normalize either shape to an
 * array so callers never have to guard against a non-iterable value. */
function toArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    for (const key of ['tasks', 'runs', 'chats', 'items', 'results', 'data']) {
      const v = (data as Record<string, unknown>)[key];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}

// dateFrom/dateTo are YYYY-MM-DD strings — omit either to let the backend default to
// the past 7 days.
export const getAgentKanbanTasks = (agentId: string, agentName: string, limit = 50, dateFrom?: string, dateTo?: string) => {
  const range = dateFrom && dateTo ? `&date_from=${dateFrom}&date_to=${dateTo}` : "";
  return api<unknown>(`/agents/${agentId}/kanban-tasks?agent_name=${encodeURIComponent(agentName)}&limit=${limit}${range}`)
    .then(toArray<AgentKanbanTaskRecord>);
};

export const getAgentRoutineRuns = (agentId: string, agentName: string, limit = 50, dateFrom?: string, dateTo?: string) => {
  const range = dateFrom && dateTo ? `&date_from=${dateFrom}&date_to=${dateTo}` : "";
  return api<unknown>(`/agents/${agentId}/routine-runs?agent_name=${encodeURIComponent(agentName)}&limit=${limit}${range}`)
    .then(toArray<AgentRoutineRunRecord>);
};

/** One page of an activity list, for infinite-scroll lists. */
export interface ActivityPage<T> {
  items: T[];
  /** Every record in the range, when the API reports it. */
  total?: number;
  hasMore: boolean;
}

function toActivityPage<T>(data: unknown, limit: number): ActivityPage<T> {
  const items = toArray<T>(data);
  const meta = (data && typeof data === 'object' ? data : {}) as { total?: unknown; has_more?: unknown };
  // Older API versions don't send has_more (and their total is just the page
  // size), so a full page then means "there may be more".
  if (typeof meta.has_more !== 'boolean') return { items, hasMore: items.length === limit };
  return { items, total: typeof meta.total === 'number' ? meta.total : undefined, hasMore: meta.has_more };
}

const pageQuery = (limit: number, offset: number, dateFrom?: string, dateTo?: string) =>
  `limit=${limit}${offset > 0 ? `&offset=${offset}` : ''}${dateFrom && dateTo ? `&date_from=${dateFrom}&date_to=${dateTo}` : ''}`;

export const getAgentKanbanTasksPage = (
  agentId: string, agentName: string, limit: number, offset = 0, dateFrom?: string, dateTo?: string,
): Promise<ActivityPage<AgentKanbanTaskRecord>> =>
  api<unknown>(`/agents/${agentId}/kanban-tasks?agent_name=${encodeURIComponent(agentName)}&${pageQuery(limit, offset, dateFrom, dateTo)}`)
    .then((data) => toActivityPage<AgentKanbanTaskRecord>(data, limit));

export const getAgentRoutineRunsPage = (
  agentId: string, agentName: string, limit: number, offset = 0, dateFrom?: string, dateTo?: string,
): Promise<ActivityPage<AgentRoutineRunRecord>> =>
  api<unknown>(`/agents/${agentId}/routine-runs?agent_name=${encodeURIComponent(agentName)}&${pageQuery(limit, offset, dateFrom, dateTo)}`)
    .then((data) => toActivityPage<AgentRoutineRunRecord>(data, limit));

// Confirmed live shape: { agent_id, total, items: [{ id, title, created, description }] }
// — distinct from ChatSessionRecord (/sessions), which uses session_id/created_at/last_active_at/message_count.
export interface AgentChatRecord {
  id:           string;
  title:        string;
  created:      string;
  description?: string;
}
export const getAgentChats = (agentId: string, limit = 50, dateFrom?: string, dateTo?: string, offset = 0) => {
  const range = dateFrom && dateTo ? `&date_from=${dateFrom}&date_to=${dateTo}` : "";
  const skip = offset > 0 ? `&offset=${offset}` : "";
  return api<unknown>(`/agents/${agentId}/chats?limit=${limit}${skip}${range}`)
    .then(toArray<AgentChatRecord>)
    .then((chats) => chats.map((c) => ({ ...c, title: stripAreaContext(c.title || '') })));
};

export type AgentChatsPage = ActivityPage<AgentChatRecord>;
/** One page of an agent's chats (newest activity first) for "load more" lists.
 * dateFrom/dateTo (YYYY-MM-DD) are optional; omit both for all time. */
export const getAgentChatsPage = (agentId: string, limit: number, offset = 0, dateFrom?: string, dateTo?: string): Promise<AgentChatsPage> =>
  api<unknown>(`/agents/${agentId}/chats?${pageQuery(limit, offset, dateFrom, dateTo)}`)
    .then((data) => {
      const page = toActivityPage<AgentChatRecord>(data, limit);
      return { ...page, items: page.items.map((c) => ({ ...c, title: stripAreaContext(c.title || '') })) };
    });

/** Confirmed live shape: { agent_id, agent_name, kanban_tasks, routine_runs, chats }
 * — plain totals per category, no status breakdown. */
export interface AgentActivitySummary {
  agent_id?: string;
  agent_name?: string;
  kanban_tasks?: number;
  routine_runs?: number;
  chats?: number;
}
export const getAgentActivitySummary = (agentId: string, agentName: string) =>
  api<AgentActivitySummary>(`/agents/${agentId}/activity-summary?agent_name=${encodeURIComponent(agentName)}`);

// ── Dashboard stats (sidebar) ────────────────────────────────────────────────

export interface DashboardStats {
  total_agents:   number;
  premade_count:  number;
  custom_count:   number;
  total_tasks:    number;
  total_runs:     number;
  success_runs:   number;
  failed_runs:    number;
  total_chats:    number;
  success_rate:   number;
}

export interface DashboardTopAgent {
  agent_id:       string;
  name:           string;
  is_system:      boolean;
  is_primary_agent: boolean;
  success_rate:   number;
  runs:           number;
  tasks:          number;
}

export interface DashboardRecentCommand {
  id:          number | string;
  task_number: string | null;
  title:       string | null;
  status_id:   string | null;
  agent_name:  string | null;
  created_at:  string | null;
}

export interface DashboardRecentRun {
  id:           number | string;
  name:         string | null;
  status:       string | null;
  agent_name:   string | null;
  started_at:   string | null;
  finished_at:  string | null;
  duration_sec: number | null;
}

export interface DashboardRecentChat {
  id:            string;
  agent_id:      string | null;
  title:         string | null;
  created:       string | null;
  last_accessed: string | null;
  description:   string | null;
}

export interface DwDashboardResponse {
  stats:     DashboardStats;
  top_agent: DashboardTopAgent | null;
}

/** Stats + top agent only — agent list is fetched separately via listDwAgents.
 * Wrapped in shareInFlight so the DW page and sidebar share one request. */
export const fetchDwDashboard = () =>
  shareInFlight(
    `dwDashboard|${localStorage.getItem('authToken') || ''}`,
    () => api<DwDashboardResponse>('/agents/dashboard'),
    REUSE_LIST_MS,
  );

/** All-activity response returned by ?tab=all — one call for the default "All" tab. */
export interface DashboardAllActivity {
  commands: DashboardRecentCommand[];
  runs:     DashboardRecentRun[];
  chats:    DashboardRecentChat[];
}

/** Lazy-load activity for sidebar tabs.
 * 'all' returns {commands, runs, chats} in one call (used on page load).
 * Individual tabs return {items: [...]} (called on tab click). */
export function fetchDwDashboardActivity(tab: 'all'): Promise<DashboardAllActivity>;
export function fetchDwDashboardActivity(tab: 'commands'): Promise<{ items: DashboardRecentCommand[] }>;
export function fetchDwDashboardActivity(tab: 'runs'): Promise<{ items: DashboardRecentRun[] }>;
export function fetchDwDashboardActivity(tab: 'chats'): Promise<{ items: DashboardRecentChat[] }>;
export function fetchDwDashboardActivity(tab: 'commands' | 'runs' | 'chats'): Promise<{ items: (DashboardRecentCommand | DashboardRecentRun | DashboardRecentChat)[] }>;
export function fetchDwDashboardActivity(tab: 'all' | 'commands' | 'runs' | 'chats'): Promise<unknown> {
  return api(`/agents/dashboard/activity?tab=${tab}`);
}

// ── Artifacts dashboard — 2-phase progressive loading ────────────────────────
// Phase 1 (summary): agents + counts + stats → page shell renders instantly
// Phase 2 (activity): per-agent feeds + global tables → fills card feeds + tables

export interface ArtifactsAgentCard {
  agent_id:         string;
  name:             string;
  is_system:        boolean;
  is_primary_agent: boolean;
  is_voice_agent:   boolean;
  harness_type:     string;
  persona_key:      string | null;
  counts: {
    kanban_tasks:  number;
    routine_runs:  number;
    success_runs:  number;
    failed_runs:   number;
    chats:         number;
    success_rate:  number;
  };
  last_active:     string | null;
}

/** Phase 1 response — lightweight, ~100-200ms. */
export interface ArtifactsSummaryResponse {
  agents: ArtifactsAgentCard[];
  stats:  DashboardStats;
}

export interface ArtifactsActivityItem {
  type:         'task' | 'run' | 'chat';
  id:           string | number;
  title:        string;
  status_id?:   string | null;
  status?:      string | null;
  time:         string | null;
  task_number?: string | null;
  routine_id?:  string | null;
  agent_name?:  string;
}

export interface ArtifactsCommand {
  id:          string | number;
  task_number: string | null;
  title:       string;
  status_id:   string | null;
  agent_name:  string | null;
  agent_id:    string;
  created_at:  string | null;
  updated_at:  string | null;
}

export interface ArtifactsRun {
  id:           string | number;
  name:         string | null;
  routine_id:   string | null;
  status:       string | null;
  agent_name:   string | null;
  agent_id:     string;
  started_at:   string | null;
  finished_at:  string | null;
  duration_sec: number | null;
}

export interface ArtifactsChat {
  id:            string;
  agent_id?:     string;
  agent_name?:   string;
  title:         string;
  created:       string | null;
  last_accessed: string | null;
}

/** Phase 2 response — slightly slower, window functions + Mongo pipelines. */
export interface ArtifactsActivityResponse {
  agent_feeds:     Record<string, ArtifactsActivityItem[]>;
  recent_activity: ArtifactsActivityItem[];
  commands:        ArtifactsCommand[];
  runs:            ArtifactsRun[];
  chats:           ArtifactsChat[];
}

function artifactsDateQs(dateFrom?: string, dateTo?: string): string {
  const p = new URLSearchParams();
  if (dateFrom) p.set('date_from', dateFrom);
  if (dateTo) p.set('date_to', dateTo);
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

/** Phase 1: agents + counts + stats.  Fast. */
export const fetchArtifactsSummary = (dateFrom?: string, dateTo?: string) =>
  api<ArtifactsSummaryResponse>(`/agents/artifacts-summary${artifactsDateQs(dateFrom, dateTo)}`);

/** Phase 2: per-agent feeds + global tables.  Fires in parallel with phase 1. */
export const fetchArtifactsActivity = (dateFrom?: string, dateTo?: string) =>
  api<ArtifactsActivityResponse>(`/agents/artifacts-activity${artifactsDateQs(dateFrom, dateTo)}`);

export interface ChatMessageRecord {
  role:       'user' | 'assistant';
  content:    string;
  created_at: string;
  file_data?: { name: string; url: string }[] | null;
}
export const getSessionMessages = (sessionId: string) =>
  api<ChatMessageRecord[]>(`/sessions/${sessionId}/messages`);

// ── Chat (SSE via POST /chat) ──────────────────────────────────────────────────
// Extra context fields that only some chat surfaces have available (AMS-scoped
// pages carry rate/channel/ampId context; a bare DW agent chat doesn't). Every
// field in this contract is always sent — callers without a value for one just
// get an empty string on the wire instead of the field being omitted.
/** Hardcoded prompt sent to /claude-managed/chat when the user clicks the
 * "Generate artifact" button under a response flagged can_generate_artifact —
 * asks the agent to export its previous answer as a downloadable file. */
export const ARTIFACT_GENERATION_PROMPT =
  'Create a downloadable file from your previous response in this conversation. ' +
  'Choose the most suitable format (Excel/CSV for tabular data, PDF or Word for reports and documents), ' +
  'generate the file, and upload it with the upload_session_file tool so I can download it.';

export interface StreamChatExtra {
  channelId?: string;
  rateToken?: string;
  nowcertsAmsUrl?: string;
  userConfirmation?: string;
  ampId?: string;
}

export async function streamChat(
  text: string,
  sessionId: string | null,
  onEvent: (e: SseEvent) => void,
  signal?: AbortSignal,
  title?: string,
  fileUrls?: string[],
  fileNames?: string[],
  ncToken?: string,
  agentId?: string,
  extra?: StreamChatExtra,
): Promise<void> {
  const formData = new FormData();
  formData.append('human_prompt', text);
  // sessionId is the conversation's own id (the one already used in the chat's
  // URL) — sent as conversationId on every turn once known.
  formData.append('conversationId', sessionId ?? '');
  formData.append('title', (title ?? '').slice(0, 60));
  formData.append('nc_token', ncToken ?? '');
  formData.append('agent_id', agentId ?? '');
  formData.append('channel_id', extra?.channelId ?? '');
  formData.append('rate_token', extra?.rateToken ?? '');
  formData.append('nowcerts_ams_url', extra?.nowcertsAmsUrl ?? '');
  formData.append('user_confirmation', extra?.userConfirmation ?? '');
  formData.append('amp_id', extra?.ampId ?? '');
  const filePaths = fileUrls?.length
    ? fileUrls.map((url, i) => ({ fileName: fileNames?.[i] ?? '', fileURL: url }))
    : [];
  formData.append('file_paths', JSON.stringify(filePaths));

  const res = await fetch(`${BASE}/chat`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token()}` }, // no Content-Type — browser sets the multipart boundary
    body: formData,
    signal,
  });

  await pumpSse(res, onEvent);
}
